import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import { WizardStatus } from '@risksmart-app/domain/src/types/consts';
import { AssessmentActivityStatus as Assessment_Activity_Status_Enum } from '@risksmart-app/domain/src/types/consts/assessment-activity-status';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import { type FC, useRef } from 'react';
import { useNavigate } from 'react-router';
import { ownerIds } from 'src/components/form';
import { PageWrapper } from 'src/components/form/form/PageWrapper';
import AssessmentRCSAActivityForm from 'src/pages/assessments/forms/assessment-rcsa-activity-form';
import type { AssessmentRCSAActivityFormDataFields } from 'src/pages/assessments/forms/assessment-rcsa-activity-form/assessmentRCSAActivitySchema';

import { useInsertAssessmentActivityWithLinkedItems } from '@/hooks/mutations/assessment-activity/useInsertAssessmentActivityWithLinkedItems';
import { useFileUpdate } from '@/hooks/mutations/file/useFileUpdate';
import { useInsertWizard } from '@/hooks/mutations/wizard';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useIsFormPrefillEnabled } from '@/hooks/useIsFormPrefillEnabled';
import {
  type CreatedEntityDestination,
  CreatedEntityFileSaveError,
  saveFilesForCreatedEntity,
} from '@/utils/createWithFiles';

import type { AssessmentTypeEnum } from '../../../../types';
import { useAssessmentTypeConfig } from '../../../../useAssessmentTypeConfig';

interface Props {
  assessmentMode: AssessmentTypeEnum;
}

const createAttemptSnapshot = (
  variables: AssessmentRCSAActivityFormDataFields,
  assessmentId: string
) =>
  cloneDeep({
    assessmentId,
    ...variables,
    // `files` is a permitted condition target, and FormContext nulls every
    // hidden field on submit, so a conditional attach-files control arrives
    // here as null rather than []. Reading it unguarded threw before the
    // insert was ever issued, failing the save outright (COI-530).
    files: (variables.files ?? []).map((file) => {
      if (file instanceof File) {
        const fileWithMeta: File & { meta?: Record<string, string> } = file;

        return {
          kind: 'new-file',
          name: file.name,
          size: file.size,
          type: file.type,
          lastModified: file.lastModified,
          meta: fileWithMeta.meta,
        };
      }

      return file;
    }),
  });

type ActivityProgress = {
  attemptSnapshot: ReturnType<typeof createAttemptSnapshot>;
  createdEntity: CreatedEntityDestination;
  isFilesSaved: boolean;
  isWizardLinked: boolean;
};

const AssessmentRCSACreateTab: FC<Props> = ({ assessmentMode }) => {
  const navigate = useNavigate();
  const assessmentId = useGetGuidParam('assessmentId');
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const formPrefillEnabled = useIsFormPrefillEnabled();
  const { updateFiles } = useFileUpdate();
  const { insertWizard } = useInsertWizard();
  const { insertAssessmentActivity } =
    useInsertAssessmentActivityWithLinkedItems();
  const {
    routing: { activityEditUrl, activityRegisterUrl },
  } = useAssessmentTypeConfig(assessmentMode);
  const activityProgressByRiskIdRef = useRef(
    new Map<string, ActivityProgress>()
  );

  const onSave = async (variables: AssessmentRCSAActivityFormDataFields) => {
    const { files } = variables;
    const attemptSnapshot = createAttemptSnapshot(variables, assessmentId);
    const createdEntities: { id: string; destination: string }[] = [];

    for (const riskId of variables.RiskIds) {
      let progress = activityProgressByRiskIdRef.current.get(riskId.value);
      if (progress && !isEqual(progress.attemptSnapshot, attemptSnapshot)) {
        throw new CreatedEntityFileSaveError({
          ...progress.createdEntity,
          cause: new Error(
            'Form values changed after the assessment activity was created'
          ),
          failureStage: 'validation',
        });
      }
      if (!progress) {
        const result = await insertAssessmentActivity({
          ...variables,
          ParentId: assessmentId,
          Status: Assessment_Activity_Status_Enum.NotStarted,
          LinkedItemIds: [riskId.value],
          CustomAttributeData: variables.CustomAttributeData || undefined,
          RiskId: riskId.value,
          IsRCSA: true,
          ...ownerIds(variables),
        });
        const activityId = result?.insertAssessmentActivityWithLinkedItems?.Id;
        if (!activityId) {
          throw new Error('Assessment Activity id is missing');
        }

        progress = {
          attemptSnapshot,
          createdEntity: {
            id: activityId,
            destination: activityEditUrl(assessmentId, activityId),
          },
          isFilesSaved: false,
          isWizardLinked: false,
        };
        activityProgressByRiskIdRef.current.set(riskId.value, progress);
      }

      const { createdEntity } = progress;
      if (!progress.isFilesSaved) {
        await saveFilesForCreatedEntity({
          createdEntity,
          saveFiles: () =>
            updateFiles({
              parentId: createdEntity.id,
              parentType: Parent_Type_Enum.AssessmentActivity,
              selectedFiles: files,
              originalFiles: [],
            }),
        });
        progress.isFilesSaved = true;
      }

      if (!progress.isWizardLinked) {
        try {
          await insertWizard({
            object: {
              RiskId: riskId.value,
              AssessmentId: assessmentId,
              ActivityId: createdEntity.id,
              Status: WizardStatus.Planned,
            },
          });
          progress.isWizardLinked = true;
        } catch (cause) {
          throw new CreatedEntityFileSaveError({
            ...createdEntity,
            cause,
            failureStage: 'linking',
          });
        }
      }
      createdEntities.push(createdEntity);
    }

    const firstCreatedEntity = createdEntities[0];
    if (!firstCreatedEntity) {
      throw new Error('Assessment Activity id is missing');
    }

    return {
      savedIds: createdEntities.map(({ id }) => id),
      createdEntity: firstCreatedEntity,
    };
  };

  const onDismiss = () => navigate(activityRegisterUrl(assessmentId));

  return (
    <AssessmentRCSAActivityForm
      onSave={onSave}
      onDismiss={onDismiss}
      publishToolbarState={objectToolbarEnabled}
      publishActiveForm={formPrefillEnabled}
      renderTemplate={(renderProps) => <PageWrapper {...renderProps} />}
    />
  );
};

export default AssessmentRCSACreateTab;
